import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { createSelect2Manager } from "../lib/select2_manager"
import { showAlertModal } from "../lib/custom_confirm"

// Category-aware context fields on the New Support Ticket form.
//   Leads      → Related Orders (≥1 required) + "Leads Issue" sub-reason
//                (required) + dependent "Specific Leads" picker
//   Billing    → Related Orders (optional)
//   everything → all hidden and cleared, so nothing extra is submitted
//
// Orders and leads are MULTI-selects (Select2): one ticket carries every
// order/lead the issue concerns — never one ticket per selection. Selecting
// orders fetches their leads (scoped server-side to the current user), grouped
// per order so the lead picker can render optgroups.
//
// Select2 fires jQuery `change` events, which native Stimulus actions cannot
// hear — so both selects are wired via jQuery in the Select2 manager's
// onInitialized callback instead of `data-action`.
//
// When the sub-reason is "Lead replacement", at least one lead becomes required
// and every selected lead is batch-checked for an existing replacement request
// (did the agent click the Lead Replacement button on it?). Leads still missing
// one are listed in a warning with per-lead deep links into the canonical
// replacement flow, and the form's submit is HARD-BLOCKED (styled popup) until
// each selected lead has a request. The server re-checks authoritatively on
// create, so an unknown/stale status here never lets a ticket through — which
// is also why an unknown status doesn't block client-side.
//
// Deflection before a ticket is filed (#15143) — never a block:
//   • A lead whose request is still pending shows the measured review wait
//     ("usually decided within N hours") and that an email follows the decision.
//   • With no lead linked yet, the agent is asked to pick it first.
// Lead Quality is not offered under "Leads Issue" (#15308), so it has no
// Replace-button deflection.
// Next step for a selected lead the Replace button refuses (never-replaceable,
// out of window, …): the Lead Replacement gate still applies to it, so point
// the agent at the ticket type that is open for any lead.
const INELIGIBLE_NEXT_STEP =
  "To report a problem with a lead that can't be returned, choose Other under Leads Issue instead."

export default class extends Controller {
  static targets = [
    "category",
    "orderGroup",
    "orderSelect",
    "orderRequiredMark",
    "issueTypeGroup",
    "issueType",
    "leadGroup",
    "leadSelect",
    "leadLabel",
    "leadRequiredMark",
    "replacementNudge",
    "replacementMissingList",
    "replacementStatus",
    "replacementStatusText",
    "replacementAppeal",
    "replacementAppealLink",
    "replacementPickLead",
    "replacementPending",
    "replacementPendingList",
  ]
  static values = {
    orderLeadsUrl: String,
    replacementStatusUrl: String,
    leadsPath: String,
    // My Replacements (#11584) — the "Appeal the denial instead" deep link.
    leadReplacementsPath: String,
  }

  // Tracks the order set whose leads we're currently fetching, so an
  // out-of-order (stale) response from a previous selection can be discarded.
  #pendingOrderKey = null
  // Same idea for the replacement-status fetch.
  #pendingReplacementKey = null
  // Latest fetched replacement statuses, lead id (string) → { clicked, status }.
  #replacementStatuses = new Map()

  connect() {
    this.select2Manager = createSelect2Manager({ nativeOnTouch: true })
    this.#initSelect2(this.orderSelectTarget, () => this.orderChanged())
    this.#initSelect2(this.leadSelectTarget, () => this.leadChanged())
    this.form = this.element.closest("form")
    this.form?.addEventListener("submit", this.#gateReplacementSubmit)
    this.categoryChanged()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.#gateReplacementSubmit)
    this.select2Manager?.destroyAll()
  }

  categoryChanged() {
    const category = this.categoryTarget.value
    const showOrder = category === "leads" || category === "billing"
    const isLeads = category === "leads"
    const orderRequired = isLeads

    this.#toggle(this.orderGroupTarget, showOrder)
    this.orderSelectTarget.required = orderRequired
    this.#toggle(this.orderRequiredMarkTarget, orderRequired)

    // "Leads Issue" is a Leads-only, required field.
    this.#toggle(this.issueTypeGroupTarget, isLeads)
    this.issueTypeTarget.required = isLeads
    if (!isLeads) this.issueTypeTarget.value = ""

    // Don't submit orders the customer can no longer see.
    if (!showOrder) this.#clearSelection(this.orderSelectTarget, () => this.orderChanged())

    this.refreshLeadPicker()
  }

  orderChanged() {
    this.refreshLeadPicker()
  }

  issueTypeChanged() {
    this.#syncReplacementUi()
  }

  leadChanged() {
    this.#syncReplacementUi()
  }

  // The lead picker only appears for Leads tickets once at least one order is
  // chosen. Leads come back grouped per selected order.
  async refreshLeadPicker() {
    const isLeads = this.categoryTarget.value === "leads"
    const orderIds = this.#selectedValues(this.orderSelectTarget)

    if (!isLeads || orderIds.length === 0) {
      this.#resetLeadPicker()
      this.#syncReplacementUi()
      return
    }

    const orderKey = orderIds.join(",")
    this.#pendingOrderKey = orderKey
    // Capture the current selection BEFORE the loading placeholder wipes the
    // options, so still-valid leads survive an order-set change.
    const previousSelection = this.#selectedValues(this.leadSelectTarget)
    this.#showLeadsLoading()
    const query = orderIds.map((id) => `lead_order_ids[]=${encodeURIComponent(id)}`).join("&")
    const response = await get(`${this.orderLeadsUrlValue}?${query}`, { responseKind: "json" })
    // Discard a stale response if the selection changed while this was in flight.
    if (this.#pendingOrderKey !== orderKey) return
    if (!response.ok) {
      this.#resetLeadPicker()
      this.#syncReplacementUi()
      return
    }

    const groups = await response.json
    if (this.#pendingOrderKey !== orderKey) return

    this.#populateLeads(groups, previousSelection)
    this.#toggle(this.leadGroupTarget, true)
    this.#syncReplacementUi()
  }

  // Keep the leads field's required state in sync, batch-check every selected
  // lead's replacement status, and show the warning for the ones still missing.
  async #syncReplacementUi() {
    const isReplacement = this.#isReplacementTicket()

    // Only require leads while their picker is actually visible. The picker is
    // hidden until an order is chosen (and again if the order_leads fetch fails),
    // and a hidden-but-required field blocks submit with no visible browser error.
    // Orders are independently required for Leads, and the server re-checks this,
    // so gating on visibility can't let a replacement ticket through without a lead.
    const leadVisible = !this.leadGroupTarget.classList.contains("d-none")
    this.#setLeadRequired(isReplacement && leadVisible)

    const leadIds = this.#selectedValues(this.leadSelectTarget)
    if (!isReplacement || leadIds.length === 0) {
      this.#hideReplacementUi()
      // No lead linked yet: ask for it first, so the check below can run.
      this.#toggleOptional("replacementPickLead", isReplacement)
      return
    }
    this.#toggleOptional("replacementPickLead", false)

    const key = leadIds.join(",")
    this.#pendingReplacementKey = key
    const query = leadIds.map((id) => `lead_ids[]=${encodeURIComponent(id)}`).join("&")
    const response = await get(`${this.replacementStatusUrlValue}?${query}`, { responseKind: "json" })
    // Discard a stale response if the selection changed while this was in flight.
    if (this.#pendingReplacementKey !== key) return
    if (!response.ok) {
      this.#hideReplacementUi()
      return
    }

    const payload = await response.json
    if (this.#pendingReplacementKey !== key) return

    this.#replacementStatuses = new Map(
      (payload.statuses || []).map((entry) => [String(entry.lead_id), entry])
    )
    this.#renderReplacementUi()
  }

  #renderReplacementUi() {
    const pending = this.#renderPendingWait()

    const missing = this.#leadsMissingReplacement()
    if (missing.length > 0) {
      // At least one selected lead was never run through the replacement flow —
      // list each with its own deep link (or, when the Replace button cannot
      // take it, the checker's reason). The submit gate blocks until resolved.
      this.#toggle(this.replacementStatusTarget, false)
      this.replacementMissingListTarget.innerHTML = missing.map((lead) => this.#missingLeadLink(lead)).join("")
      this.#toggle(this.replacementNudgeTarget, true)
      this.#renderAppealNudge(null)
      return
    }

    this.#toggle(this.replacementNudgeTarget, false)
    // All covered — surface the existing request's status for context, but only
    // for a single-lead selection (several leads have several statuses), and
    // not when the pending-wait line already says it.
    const selected = this.#selectedLeads()
    const single = selected.length === 1 ? this.#replacementStatuses.get(selected[0].id) : null
    if (single?.clicked && pending.length === 0) {
      this.replacementStatusTextTarget.textContent = single.status || "requested"
      this.#toggle(this.replacementStatusTarget, true)
    } else {
      this.#toggle(this.replacementStatusTarget, false)
    }
    this.#renderAppealNudge(single)
  }

  // #15143: "Your replacement for this lead is in review, usually decided
  // within N hours; you'll get an email when it's decided." — one line per
  // selected lead whose request is still pending, built server-side from the
  // measured estimate. Names the lead when several are selected. Returns the
  // pending leads it rendered.
  #renderPendingWait() {
    if (!this.hasReplacementPendingTarget) return []

    const selected = this.#selectedLeads()
    const pending = selected.filter((lead) => this.#replacementStatuses.get(lead.id)?.review_message)
    if (this.hasReplacementPendingListTarget) {
      this.replacementPendingListTarget.innerHTML = pending
        .map((lead) => {
          const message = this.#escape(this.#replacementStatuses.get(lead.id).review_message)
          const name = selected.length > 1 ? `<strong>${this.#escape(lead.name)}:</strong> ` : ""
          return `<p class="small mb-1">${name}${message}</p>`
        })
        .join("")
    }
    this.#toggle(this.replacementPendingTarget, pending.length > 0)
    return pending
  }

  // "Appeal the denial instead" (#11584): a denied-and-still-appealable
  // replacement has a self-service path that beats a ticket. Shown beside the
  // status line; it never blocks the ticket.
  #renderAppealNudge(single) {
    if (!this.hasReplacementAppealTarget) return

    const appealable = Boolean(single?.clicked && single.appealable)
    if (appealable && this.hasReplacementAppealLinkTarget) {
      this.replacementAppealLinkTarget.setAttribute("href", this.#appealUrl(single.replacement_id))
    }
    this.#toggle(this.replacementAppealTarget, appealable)
  }

  #appealUrl(replacementId) {
    const base = this.leadReplacementsPathValue || "/lead_replacements"
    return replacementId ? `${base}?open_replacement=${encodeURIComponent(replacementId)}` : base
  }

  #missingLeadLink(lead) {
    const entry = this.#replacementStatuses.get(lead.id)
    if (entry?.eligible === false && entry.reason) {
      // The Replace button cannot take this lead — say why instead of linking
      // to a flow that would refuse it, and name the next step: a lead nobody
      // may replace can't carry a Lead Replacement ticket, but an Other
      // ticket about it is always open.
      return (
        `<span class="small">${this.#escape(lead.name)}: ${this.#escape(entry.reason)} ` +
        `${INELIGIBLE_NEXT_STEP}</span>`
      )
    }
    return (
      `<a class="gl-btn gl-btn-primary gl-btn-sm" target="_blank" rel="noopener" href="${this.#replacementUrl(lead.id)}">` +
      `Start replacement — ${this.#escape(lead.name)}<i class="ph ph-arrow-right ms-1"></i></a>`
    )
  }

  // Hard gate on the form's submit (fired by the modal footer's requestSubmit):
  // a replacement ticket may not be created while a selected lead has no
  // replacement request. Cancelling the event stops Turbo before any request.
  // Leads with an unknown status (fetch failed / still in flight) do NOT block
  // here — the server enforces the same rule authoritatively and re-renders.
  #gateReplacementSubmit = (event) => {
    const missing = this.#leadsMissingReplacement()
    if (missing.length === 0) return

    event.preventDefault()
    // A lead the Replace button refuses cannot be "resolved" by clicking it, so
    // the popup says why and points at Other instead of asking for a
    // click that would only be refused again.
    const ineligible = missing.filter((lead) => this.#replacementStatuses.get(lead.id)?.eligible === false)
    const startable = missing.filter((lead) => !ineligible.includes(lead))
    const parts = []
    if (startable.length > 0) {
      parts.push(
        `You haven't clicked the Lead Replacement button on: ${startable.map((lead) => lead.name).join(", ")}. ` +
          "Please resolve lead replacements by clicking the Lead Replacement button on each lead first."
      )
    }
    if (ineligible.length > 0) {
      parts.push(
        `These leads can't be returned with the Replace button: ${ineligible.map((lead) => lead.name).join(", ")}. ` +
          INELIGIBLE_NEXT_STEP
      )
    }
    showAlertModal(parts.join(" "), { title: "Replacement request required" })
  }

  #leadsMissingReplacement() {
    if (!this.#isReplacementTicket()) return []

    return this.#selectedLeads().filter(
      (lead) => this.#replacementStatuses.get(lead.id)?.clicked === false
    )
  }

  #isReplacementTicket() {
    return this.categoryTarget.value === "leads" && this.issueTypeTarget.value === "lead_replacement"
  }

  // Selected leads as { id, name }. The option label is Support::LeadOptionLabel's
  // "Name · State · Date" — the first segment is the customer name.
  #selectedLeads() {
    return Array.from(this.leadSelectTarget.selectedOptions)
      .filter((option) => option.value !== "")
      .map((option) => ({
        id: option.value,
        name: (option.textContent || "").split(" · ")[0].trim() || `Lead #${option.value}`,
      }))
  }

  #replacementUrl(leadId) {
    const base = this.leadsPathValue || "/leads"
    return `${base}?open_lead=${encodeURIComponent(leadId)}&open_replacement=1`
  }

  #setLeadRequired(required) {
    this.leadSelectTarget.required = required
    this.#toggle(this.leadRequiredMarkTarget, required)
    if (this.hasLeadLabelTarget) {
      this.leadLabelTarget.textContent = required ? "Specific Leads" : "Specific Leads (Optional)"
    }
  }

  #hideReplacementUi() {
    this.#pendingReplacementKey = null // invalidate any in-flight fetch
    this.#replacementStatuses = new Map()
    this.#toggle(this.replacementNudgeTarget, false)
    this.#toggle(this.replacementStatusTarget, false)
    if (this.hasReplacementAppealTarget) this.#toggle(this.replacementAppealTarget, false)
    this.#toggleOptional("replacementPending", false)
  }

  // Toggle a target this controller added later (#15143), so a cached
  // older form without it keeps working.
  #toggleOptional(name, show) {
    const has = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    if (this[has]) this.#toggle(this[`${name}Target`], show)
  }

  // Shown (disabled) while the leads fetch is in flight — a blank, silent
  // picker reads as broken and invites re-clicking the order select.
  #showLeadsLoading() {
    this.leadSelectTarget.innerHTML = '<option value="" disabled>Loading leads…</option>'
    this.leadSelectTarget.disabled = true
    this.#toggle(this.leadGroupTarget, true)
    this.#refreshSelect2(this.leadSelectTarget, () => this.leadChanged())
  }

  // Rebuild the lead options from the per-order groups, keeping any still-valid
  // selection. A single order renders flat options; several orders render one
  // optgroup per order so the agent can tell same-named leads apart.
  #populateLeads(groups, previousSelection = []) {
    const previous = new Set(previousSelection)
    const list = groups || []
    const optionHtml = (lead) =>
      `<option value="${lead.id}"${previous.has(String(lead.id)) ? " selected" : ""}>${this.#escape(lead.label)}</option>`

    let html
    if (list.length > 1) {
      html = list
        .map((group) => `<optgroup label="${this.#escape(group.order_label)}">${(group.leads || []).map(optionHtml).join("")}</optgroup>`)
        .join("")
    } else {
      html = ((list[0] && list[0].leads) || []).map(optionHtml).join("")
    }

    this.leadSelectTarget.innerHTML = html
    this.leadSelectTarget.disabled = false
    this.#refreshSelect2(this.leadSelectTarget, () => this.leadChanged())
  }

  #resetLeadPicker() {
    this.#pendingOrderKey = null // invalidate any in-flight fetch
    this.leadSelectTarget.innerHTML = ""
    this.leadSelectTarget.disabled = false
    this.#toggle(this.leadGroupTarget, false)
    this.#refreshSelect2(this.leadSelectTarget, () => this.leadChanged())
  }

  #initSelect2(element, onChange) {
    // When the form is hosted inside a modal (the React ServerFrameFormModal
    // renders Bootstrap `.modal` markup), Select2 must anchor its dropdown to
    // the modal via `dropdownParent` — otherwise it appends the dropdown to
    // <body>, which sits behind the modal's stacking context, hiding the
    // options. On the standalone page there is no `.modal` ancestor, so this is
    // undefined and Select2 keeps its default body placement.
    const modalEl = this.element.closest(".modal")

    this.select2Manager.init(
      element,
      () => ({
        placeholder: element.dataset.select2Placeholder || "Select",
        allowClear: true,
        closeOnSelect: false,
        multiple: true,
        width: "100%",
        dropdownParent: modalEl && window.$ ? window.$(modalEl) : undefined,
      }),
      {
        onInitialized: (el, $el) => {
          const handler = () => onChange()
          $el.on("change", handler)
          return () => $el.off("change", handler)
        },
      }
    )
  }

  // Destroy + re-init so the Select2 widget re-reads the (rebuilt) options and
  // re-renders its selection pills. The manager runs the old instance's cleanup
  // (unbinding the change handler) before re-initializing, so handlers never
  // stack. A no-op when Select2 isn't active (native fallback / tests).
  #refreshSelect2(element, onChange) {
    if (!this.select2Manager) return
    this.select2Manager.destroy(element)
    this.#initSelect2(element, onChange)
  }

  #clearSelection(element, onChange) {
    if (this.#selectedValues(element).length === 0) return
    Array.from(element.options).forEach((option) => {
      option.selected = false
    })
    this.#refreshSelect2(element, onChange)
  }

  #selectedValues(select) {
    return Array.from(select.selectedOptions)
      .map((option) => option.value)
      .filter((value) => value !== "")
  }

  #toggle(el, show) {
    el.classList.toggle("d-none", !show)
  }

  // Lead labels include customer names — escape before inserting as HTML.
  #escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
